(() => {
  if (document.documentElement.hasAttribute('data-spazioil-guide-ready')) return;
  document.documentElement.setAttribute('data-spazioil-guide-ready', 'true');
  let consumeEscapeKeyup = false;
  const initializedGuides = new WeakSet();
  const furnitureDefaults = {
    sofa: 84,
    bed: 78,
    wall: 96,
    console: 64,
    dining: 72,
    fireplace: 60,
    desk: 56,
    bench: 48
  };

  let activeGuide = null;
  let activeTrigger = null;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const normalize = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const formatNumber = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const parseDimensions = (label) => {
    const text = String(label || '').replace(/[“”″]/g, '"').replace(/[′’]/g, "'").replace(/×/g, 'x');
    const matches = [...text.matchAll(/(\d+(?:\.\d+)?)\s*(cm|inches|inch|in|["']{1,2})?\s*x\s*(\d+(?:\.\d+)?)\s*(cm|inches|inch|in|["']{1,2})?/gi)];
    if (!matches.length) return { width: 24, height: 24 };
    const pairs = matches.map(match => {
      const unit = `${match[2] || ''} ${match[4] || ''}`.toLowerCase();
      return { width: Number(match[1]), height: Number(match[3]), centimeters: unit.includes('cm') };
    });
    const inches = pairs.find(pair => !pair.centimeters);
    if (inches) return inches;
    return { width: pairs[0].width / 2.54, height: pairs[0].height / 2.54 };
  };

  const inferredShape = ({ width, height }) => {
    const ratio = width / Math.max(height, 1);
    if (ratio > 1.18) return 'horizontal';
    if (ratio < 0.84) return 'vertical';
    return 'square';
  };

  const getOptionNameFromControl = (control) => {
    const name = control.getAttribute('name') || '';
    const bracketMatch = name.match(/options\[(.+)]/i);
    return normalize(bracketMatch ? bracketMatch[1] : name);
  };

  class SpazioilSizeGuide {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.optionName = root.dataset.optionName;
      this.mountParent = root.parentNode;
      this.mountNextSibling = root.nextSibling;
      this.values = this.readValues();
      this.dimensions = this.values.map(parseDimensions);
      this.state = {
        furniture: 'sofa',
        furnitureWidth: Number(root.dataset.defaultFurnitureWidth) || 84,
        sizeIndex: 0,
        frame: 'framed',
        shape: 'auto',
        layout: '1'
      };

      this.scene = root.querySelector('[data-guide-scene]');
      this.sizeSelect = root.querySelector('[data-guide-size]');
      this.widthInput = root.querySelector('[data-guide-input="furnitureWidth"]');
      this.recommendation = root.querySelector('[data-guide-recommended]');
      this.artWidthLabel = root.querySelector('[data-guide-art-width]');
      this.artHeightLabel = root.querySelector('[data-guide-art-height]');
      this.furnitureWidthLabel = root.querySelector('[data-guide-furniture-width]');
      this.caption = root.querySelector('[data-guide-caption]');
      this.panel = root.querySelector('[role="dialog"]');

      this.bindEvents();
      this.sceneObserver = new ResizeObserver(() => { if (!this.root.hidden) this.updateScene(); });
      if (this.scene) this.sceneObserver.observe(this.scene);
      this.setSelectedFromProduct();
      this.update();
    }

    mountToBody() {
      if (this.root.parentNode !== document.body) {
        document.body.appendChild(this.root);
      }
    }

    restoreMount() {
      if (this.mountParent?.isConnected) {
        const referenceNode = this.mountNextSibling?.parentNode === this.mountParent
          ? this.mountNextSibling
          : null;
        this.mountParent.insertBefore(this.root, referenceNode);
      } else {
        this.root.remove();
      }
    }

    readValues() {
      const script = this.root.querySelector('[data-size-guide-values]');
      if (!script) return [];

      try {
        const values = JSON.parse(script.textContent);
        return Array.isArray(values) ? values.map(String) : [];
      } catch (error) {
        return [];
      }
    }

    getProductInfo() {
      return document.getElementById(`ProductInfo-${this.sectionId}`);
    }

    getCurrentProductValue() {
      const expectedName = normalize(this.optionName);
      const controls = this.getProductInfo()?.querySelectorAll('select[name^="options["], input[type="radio"]:checked');
      const matchingControl = [...(controls || [])].find((control) => getOptionNameFromControl(control) === expectedName);
      return matchingControl ? matchingControl.value : '';
    }

    setSelectedFromProduct() {
      const currentValue = normalize(this.getCurrentProductValue());
      const currentIndex = this.values.findIndex((value) => normalize(value) === currentValue);
      this.state.sizeIndex = currentIndex >= 0 ? currentIndex : this.getRecommendedIndex();
      if (this.sizeSelect) this.sizeSelect.value = String(this.state.sizeIndex);
    }

    bindEvents() {
      this.root.querySelectorAll('[data-size-guide-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.root.querySelectorAll('[data-guide-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          const choice = button.dataset.guideChoice;
          const value = button.dataset.value;
          if (!choice || !value) return;

          this.state[choice] = value;
          if (choice === 'furniture') {
            this.state.furnitureWidth = furnitureDefaults[value] || this.state.furnitureWidth;
            if (this.widthInput) this.widthInput.value = String(this.state.furnitureWidth);
          }
          this.update();
        });
      });

      this.root.querySelectorAll('[data-width-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.widthStep) || 0;
          this.state.furnitureWidth = clamp(this.state.furnitureWidth + step, 24, 240);
          if (this.widthInput) this.widthInput.value = String(this.state.furnitureWidth);
          this.update();
        });
      });

      this.widthInput?.addEventListener('input', () => {
        if (this.widthInput.value.trim() === '') return;
        const value = Number(this.widthInput.value);
        if (!Number.isFinite(value)) return;
        this.state.furnitureWidth = clamp(value, 24, 240);
        if (value > 240 || value <= 0) this.widthInput.value = String(this.state.furnitureWidth);
        this.update();
      });

      ['change', 'blur'].forEach(eventName => this.widthInput?.addEventListener(eventName, () => {
        this.widthInput.value = String(this.state.furnitureWidth);
      }));

      this.sizeSelect?.addEventListener('change', () => {
        this.state.sizeIndex = clamp(Number(this.sizeSelect.value) || 0, 0, Math.max(this.values.length - 1, 0));
        this.update();
      });

      this.root.querySelector('[data-guide-apply]')?.addEventListener('click', () => {
        if (this.applySelectedSize()) this.close();
      });
    }

    open(trigger) {
      if (activeGuide && activeGuide !== this) activeGuide.close(false);
      activeGuide = this;
      activeTrigger = trigger;
      this.parentModal = trigger?.closest('quick-add-modal[open]');
      if (this.parentModal && typeof removeTrapFocus === 'function') removeTrapFocus();
      this.mountToBody();
      this.setSelectedFromProduct();
      this.root.hidden = false;
      this.root.dataset.open = 'true';
      if (this.scene) this.sceneObserver.observe(this.scene);
      if (this.panel) this.panel.scrollTop = 0;
      trigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('spazioil-size-guide-open');
      requestAnimationFrame(() => {
        this.update();
        this.root.querySelector('.spazioil-size-guide__close')?.focus();
      });
    }

    close(restoreFocus = true) {
      if (this.root.hidden) return;
      this.root.hidden = true;
      this.sceneObserver.disconnect();
      delete this.root.dataset.open;
      activeTrigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('spazioil-size-guide-open');

      if (this.parentModal?.hasAttribute('open') && typeof trapFocus === 'function') {
        trapFocus(this.parentModal, restoreFocus ? activeTrigger : this.parentModal.querySelector('[role="dialog"]'));
      } else if (restoreFocus) activeTrigger?.focus();
      this.restoreMount();
      if (activeGuide === this) activeGuide = null;
      activeTrigger = null;
    }

    getRecommendedIndex() {
      if (!this.dimensions.length) return 0;
      const targetWidth = this.state.furnitureWidth * 0.58;
      return this.dimensions.reduce((bestIndex, dimensions, index) => {
        const bestDistance = Math.abs(this.dimensions[bestIndex].width - targetWidth);
        const distance = Math.abs(dimensions.width - targetWidth);
        return distance < bestDistance ? index : bestIndex;
      }, 0);
    }

    renderRecommendation() {
      if (!this.recommendation || !this.values.length) return;
      const recommendedIndex = this.getRecommendedIndex();
      const dimensions = this.dimensions[recommendedIndex];
      const recommendedLabel = `${formatNumber(dimensions.width)}\"W × ${formatNumber(dimensions.height)}\"H`;

      [...this.sizeSelect.options].forEach((option, index) => {
        const original = this.values[index] || option.textContent;
        option.textContent = index === recommendedIndex ? `${original} — RECOMMENDED` : original;
      });

      this.recommendation.replaceChildren();
      const text = document.createTextNode('Recommended for this furniture: ');
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = recommendedLabel;
      button.addEventListener('click', () => {
        this.state.sizeIndex = recommendedIndex;
        this.sizeSelect.value = String(recommendedIndex);
        this.update();
      });
      this.recommendation.append(text, button);
    }

    updatePressedStates() {
      this.root.querySelectorAll('[data-guide-choice]').forEach((button) => {
        const choice = button.dataset.guideChoice;
        button.setAttribute('aria-pressed', String(this.state[choice] === button.dataset.value));
      });
    }

    updateScene() {
      if (!this.scene || !this.dimensions.length) return;
      const dimensions = this.dimensions[this.state.sizeIndex] || this.dimensions[0];
      const frameShape = inferredShape(dimensions);
      const sceneAspect = this.scene.clientHeight > 0 ? this.scene.clientWidth / this.scene.clientHeight : 1.5;
      const referenceWidth = Math.max(this.state.furnitureWidth / 0.55, dimensions.width / 0.42, dimensions.height * sceneAspect / 0.58, 110);
      const furniturePercent = (this.state.furnitureWidth / referenceWidth) * 100;
      const artWidthPercent = (dimensions.width / referenceWidth) * 100;
      const artHeightPercent = (dimensions.height / referenceWidth) * 100 * sceneAspect;

      this.scene.style.setProperty('--furniture-width-pct', `${furniturePercent}%`);
      this.scene.style.setProperty('--art-width-pct', `${artWidthPercent}%`);
      this.scene.style.setProperty('--art-height-pct', `${artHeightPercent}%`);
      this.root.dataset.furniture = this.state.furniture;
      this.root.dataset.frame = this.state.frame;
      this.root.dataset.shape = frameShape;
      this.root.dataset.layout = this.state.layout;

      const widthText = `${formatNumber(dimensions.width)}\" W`;
      const heightText = `${formatNumber(dimensions.height)}\" H`;
      const furnitureText = `${formatNumber(this.state.furnitureWidth)}\" W`;
      if (this.artWidthLabel) this.artWidthLabel.textContent = widthText;
      if (this.artHeightLabel) this.artHeightLabel.textContent = heightText;
      if (this.furnitureWidthLabel) this.furnitureWidthLabel.textContent = furnitureText;

      const layoutLabel = this.state.layout === '1' ? 'Single' : `Set of ${this.state.layout}`;
      if (this.caption) {
        this.caption.textContent = `${formatNumber(dimensions.width)}\"W × ${formatNumber(dimensions.height)}\"H · ${layoutLabel} · Furniture ${formatNumber(this.state.furnitureWidth)}\" wide`;
      }
    }

    update() {
      this.updatePressedStates();
      this.renderRecommendation();
      this.updateScene();
    }

    applySelectedSize() {
      const selectedValue = this.values[this.state.sizeIndex];
      if (!selectedValue) return false;

      const expectedName = normalize(this.optionName);
      const productInfo = this.getProductInfo();
      if (!productInfo) return false;
      const select = [...productInfo.querySelectorAll('select[name^="options["]')]
        .find((control) => getOptionNameFromControl(control) === expectedName);

      if (select) {
        const matchingOption = [...select.options].find((option) => normalize(option.value) === normalize(selectedValue));
        if (matchingOption) {
          select.value = matchingOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }

      const radio = [...productInfo.querySelectorAll('input[type="radio"]')]
        .find((control) => getOptionNameFromControl(control) === expectedName && normalize(control.value) === normalize(selectedValue));
      if (radio) { radio.click(); return true; }
      return false;
    }

    trapFocus(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        consumeEscapeKeyup = true;
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll(
        'button:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-spazioil-size-guide]').forEach((root) => {
      if (initializedGuides.has(root)) return;
      const guide = new SpazioilSizeGuide(root);
      root.spazioilSizeGuide = guide;
      initializedGuides.add(root);
    });

  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-size-guide-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!target) return;
    initialize(target.parentNode);
    target.spazioilSizeGuide?.open(trigger);
  });

  document.addEventListener('keydown', (event) => activeGuide?.trapFocus(event), true);
  document.addEventListener('keyup', (event) => {
    if (consumeEscapeKeyup && event.key === 'Escape') {
      event.stopImmediatePropagation();
      consumeEscapeKeyup = false;
    }
  }, true);

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('DOMContentLoaded', () => initialize());
  initialize();
})();
